import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useUserAuth } from '../../contexts/UserAuthContext';

interface Package {
  id: number;
  name: string;
  name_ar?: string;
  description: string;
  description_ar?: string;
  price: number;
  price_note?: string;
  price_note_ar?: string;
  currency: string;
  features: string[];
  features_ar?: string[];
  processing_time: string;
  processing_time_ar?: string;
  popular: boolean;
  active: boolean;
  sort_order: number;
  country_name: string;
  country_code: string;
}

interface PackagesSectionProps {
  countryCode: string;
  countryName: string;
  primaryColor: string;
  secondaryColor: string;
}

const PackagesSection: React.FC<PackagesSectionProps> = ({
  countryCode,
  countryName,
  primaryColor,
  secondaryColor,
}) => {
  const [packages, setPackages] = useState<Package[]>([]);
  const [loading, setLoading] = useState(true);
  const { i18n, t } = useTranslation();
  const navigate = useNavigate();
  const { isAuthenticated } = useUserAuth();
  
  // Helper functions to get the appropriate language content
  const getLocalizedContent = (pkg: Package, field: 'name' | 'description' | 'processing_time') => {
    if (i18n.language === 'ar') {
      const arabicField = `${field}_ar` as keyof Package;
      const arabicValue = pkg[arabicField] as string;
      if (arabicValue && arabicValue.trim() !== '') {
        return arabicValue;
      }
    }
    return pkg[field] as string;
  };
  
  const getLocalizedFeatures = (pkg: Package): string[] => {
    if (i18n.language === 'ar' && pkg.features_ar && pkg.features_ar.length > 0) {
      // Filter out empty Arabic features and fallback to English if needed
      const arabicFeatures = pkg.features_ar.filter(f => f && f.trim() !== '');
      if (arabicFeatures.length > 0) {
        return arabicFeatures;
      }
    }
    return pkg.features || [];
  };

  const getPriceNote = (pkg: Package) => {
    const note = i18n.language === 'ar' && pkg.price_note_ar && pkg.price_note_ar.trim() !== ''
      ? pkg.price_note_ar
      : pkg.price_note;
    return note && note.trim() !== '' ? note.trim() : '';
  };

  useEffect(() => {
    fetchPackages();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countryCode, i18n.language]); // Reload when language changes

  const fetchPackages = async () => {
    try {
      // Try the primary country code first
      let response = await fetch(`/backend/api/packages/?country=${countryCode}`);
      
      let data = await response.json();
      
      if (data.success && data.packages && data.packages.length > 0) {
        setPackages(data.packages);
        return;
      }

      // If no packages found, try alternative country codes
      const alternativeCodes: Record<string, string[]> = {
        'UK': ['GB', 'GBR', 'UNITED_KINGDOM'],
        'GB': ['UK', 'GBR', 'UNITED_KINGDOM'],
        'US': ['USA', 'UNITED_STATES'],
        'USA': ['US', 'UNITED_STATES'],
        'SA': ['SAU', 'SAUDI_ARABIA'],
        'SAU': ['SA', 'SAUDI_ARABIA'],
        'EG': ['EGY', 'EGYPT'],
        'EGY': ['EG', 'EGYPT']
      };

      if (alternativeCodes[countryCode]) {
        for (const altCode of alternativeCodes[countryCode]) {
          const altResponse = await fetch(`/backend/api/packages/?country=${altCode}`);
          const altData = await altResponse.json();
          
          if (altData.success && altData.packages && altData.packages.length > 0) {
            setPackages(altData.packages);
            return;
          }
        }
      }

      // No packages found for this country
      
    } catch (error) {
      // Error fetching packages - fail silently
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  // Fixed color mappings to ensure Tailwind includes these classes
  const getColorClasses = (primary: string, secondary: string) => {
    const colorMap: Record<string, any> = {
      green: {
        primary: 'text-green-500',
        secondary: 'text-emerald-600',
        border: 'border-green-500',
        borderHover: 'hover:border-green-200',
        bg: 'bg-green-600',
        bgHover: 'hover:bg-green-700',
        bgLight: 'hover:bg-green-50',
        textHover: 'hover:text-green-700',
        gradient: 'bg-gradient-to-r from-green-500 to-emerald-500',
        gradientHover: 'hover:from-green-600 hover:to-emerald-600',
        spinner: 'border-green-600'
      },
      yellow: {
        primary: 'text-yellow-500',
        secondary: 'text-red-600',
        border: 'border-yellow-500',
        borderHover: 'hover:border-yellow-200',
        bg: 'bg-yellow-600',
        bgHover: 'hover:bg-yellow-700',
        bgLight: 'hover:bg-yellow-50',
        textHover: 'hover:text-yellow-700',
        gradient: 'bg-gradient-to-r from-yellow-500 to-red-500',
        gradientHover: 'hover:from-yellow-600 hover:to-red-600',
        spinner: 'border-yellow-600'
      },
      blue: {
        primary: 'text-blue-500',
        secondary: 'text-red-600',
        border: 'border-blue-500',
        borderHover: 'hover:border-blue-200',
        bg: 'bg-blue-600',
        bgHover: 'hover:bg-blue-700',
        bgLight: 'hover:bg-blue-50',
        textHover: 'hover:text-blue-700',
        gradient: 'bg-gradient-to-r from-blue-500 to-red-500',
        gradientHover: 'hover:from-blue-600 hover:to-red-600',
        spinner: 'border-blue-600'
      }
    };
    return colorMap[primary] || colorMap.blue;
  };

  const colors = getColorClasses(primaryColor, secondaryColor);

  const handleChoosePackage = async (pkg: Package) => {
    if (isAuthenticated) {
      // User is logged in - redirect to requests page with preselected package
      const selectedPackage = {
        id: pkg.id,
        name: getLocalizedContent(pkg, 'name'),
        description: getLocalizedContent(pkg, 'description'),
        price: pkg.price,
        currency: pkg.currency,
        features: getLocalizedFeatures(pkg),
        processingTime: getLocalizedContent(pkg, 'processing_time'),
        popular: pkg.popular,
        country: {
          name: pkg.country_name,
          code: pkg.country_code,
          flag: '🌍' // Will be updated from package data
        }
      };

      navigate('/dashboard/requests', {
        state: {
          selectedPackage: selectedPackage,
          message: 'Your selected package is ready to complete.'
        }
      });
    } else {
      // User is not logged in - redirect to register page
      navigate('/register', {
        state: {
          packageId: pkg.id.toString(),
          country: pkg.country_code.toLowerCase()
        }
      });
    }
  };

  return (
    <section id="packages" className="py-16 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            {countryName} {t('services.packages.formationPackages')}
          </h2>
          <p className="text-xl text-gray-600">
            {t('services.packages.tailoredSolutions')} {countryName} {t('services.packages.market')}
          </p>
        </motion.div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className={`animate-spin rounded-full h-12 w-12 border-b-2 ${colors.spinner}`}></div>
          </div>
        ) : packages.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500 text-lg">{t('services.packages.noPackagesAvailable')} {countryName} {t('services.packages.checkBackLater')}</p>
            <p className="text-gray-400">{t('services.packages.contactCustomSolutions')}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {packages.map((pkg, index) => (
              <motion.div
                key={pkg.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                className={`relative bg-white rounded-2xl shadow-xl border-2 p-8 flex flex-col h-full ${
                  !!pkg.popular
                    ? `${colors.border} transform scale-105`
                    : `border-gray-200 ${colors.borderHover}`
                } hover:shadow-2xl transition-all duration-300`}
              >
                {!!pkg.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className={`${colors.gradient} text-white px-6 py-1 rounded-full text-sm font-semibold shadow-lg`}>
                      {t('services.packages.mostPopular')}
                    </span>
                  </div>
                )}

                <div className="text-center mb-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">
                    {getLocalizedContent(pkg, 'name')}
                  </h3>
                  <p className="text-gray-600 mb-6">{getLocalizedContent(pkg, 'description')}</p>
                  
                  <div className="mb-4">
                    <div className="flex items-center justify-center space-x-2">
                      <span className={`text-4xl font-bold ${colors.secondary}`}>
                        {formatPrice(pkg.price, pkg.currency)}
                      </span>
                    </div>
                    {getPriceNote(pkg) && (
                      <p className="text-sm text-gray-500 mt-2 text-center">{getPriceNote(pkg)}</p>
                    )}
                  </div>

                  <div className="grid grid-cols-1 gap-2 text-sm text-gray-600 mb-6">
                    <div className="flex items-center justify-between">
                      <span>{t('services.packages.timeline')}:</span>
                      <span className="font-medium text-gray-900">{getLocalizedContent(pkg, 'processing_time')}</span>
                    </div>
                  </div>
                </div>

                <ul className="space-y-4 mb-8 flex-1">
                  {getLocalizedFeatures(pkg).map((feature, idx) => (
                    <li key={idx} className="flex items-start">
                      <svg
                        className={`w-5 h-5 ${colors.primary} mr-3 flex-shrink-0 mt-0.5`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                      <span className="text-gray-700 text-sm leading-relaxed">{feature}</span>
                    </li>
                  ))}
                </ul>

                <div className="mt-auto">
                  <motion.button
                    onClick={() => handleChoosePackage(pkg)}
                    className={`w-full py-4 px-6 rounded-xl font-semibold transition-all duration-200 ${
                      !!pkg.popular
                        ? `${colors.gradient} text-white ${colors.gradientHover} shadow-lg hover:shadow-xl`
                        : `bg-gray-100 text-gray-800 ${colors.bgLight} ${colors.textHover}`
                    }`}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    {t('services.packages.choose')} {getLocalizedContent(pkg, 'name')}
                  </motion.button>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default PackagesSection;
